import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { reatomPremiumCurrencyInfo } from '@root/features/premium/currency-information'
import { useTranslation } from '@root/shared/localization'
import { PremiumDialog } from '@root/shared/ui/premium-dialog'
import { PremiumCurrencyDetails } from '@root/widgets/premium/premium-currency-details/ui/common/premium-currency-details'
import { UploadPremiumBannerDialog } from '@root/widgets/premium/update-banner-drawer/ui/desktop'
import { UpdateEmojiDialog } from '@root/widgets/premium/update-emoji-drawer/ui/desktop'
import { UploadPremiumLogoDialog } from '@root/widgets/premium/update-logo-drawer/ui/desktop'

import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'
import type { ClassNameProps } from '@root/shared/react'
import type { DialogControlProps } from '@root/shared/ui-kit'

type PremiumCurrencyDialogProps = {
  currency: CurrencyAddress
} & ClassNameProps &
  DialogControlProps

export const PremiumCurrencyDialog: React.FC<PremiumCurrencyDialogProps> = ({
  className,
  currency,
  onOpenChange,
  ...props
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomPremiumCurrencyInfo({ currencyAddress: currency }),
    [currency],
  )
  const [state] = useAtom(model.pairState)

  const [updateEmojiDialogOpen, setUpdateEmojiDialogOpen] = useState(false)
  const [updateLogoDialogOpen, setUpdateLogoDialogOpen] = useState(false)
  const [updateBannerDialogOpen, setUpdateBannerDialogOpen] = useState(false)

  return (
    <Root
      className={className}
      onOpenChange={onOpenChange}
      title={t('currency.edit_token')}
      describedBy={t('currency.edit_token')}
      onClose={() => {
        onOpenChange?.(false)
      }}
      {...props}
    >
      <Body>
        <PremiumCurrencyDetails
          onEmojiEdit={() => {
            setUpdateEmojiDialogOpen(true)
          }}
          onLogoEdit={() => {
            setUpdateLogoDialogOpen(true)
          }}
          onBannerEdit={() => {
            setUpdateBannerDialogOpen(true)
          }}
          model={model}
        />
      </Body>
      {AsyncStateN.isSuccess(state) ? (
        <>
          <UpdateEmojiDialog
            open={updateEmojiDialogOpen}
            onOpenChange={setUpdateEmojiDialogOpen}
            currency={state.value.premiumMeta.currency}
            premiumMeta={state.value.premiumMeta}
          />

          <UploadPremiumLogoDialog
            open={updateLogoDialogOpen}
            onOpenChange={setUpdateLogoDialogOpen}
            pair={state.value.pair}
            currency={state.value.premiumMeta.currency}
            premiumMeta={state.value.premiumMeta}
          />

          {state.value.pair ? (
            <UploadPremiumBannerDialog
              open={updateBannerDialogOpen}
              onOpenChange={setUpdateBannerDialogOpen}
              pair={state.value.pair}
              premiumMeta={state.value.premiumMeta}
            />
          ) : undefined}
        </>
      ) : undefined}
    </Root>
  )
}

const Root = styled(PremiumDialog)`
  min-height: 400px;
`

const Body = styled.div`
  padding-top: 16px;
`
